import { useState } from "react";

import { useEmService } from "@/hooks/machines/em";
import type { EmServiceMachine } from "@/schemas/machine";

import { EmServiceTemplate } from "./em-service.template";

export interface EmServiceProps {
	readonly machine: EmServiceMachine;
}

export const EmService = ({ machine: initialMachine }: EmServiceProps) => {
	const [machine, setMachine] = useState<EmServiceMachine>(initialMachine);
	const emService = useEmService();

	const handleToggleLongHeadCreaseClicked = async (longHeadNumber: number) => {
		const isActive = machine.creaseTools?.includes(longHeadNumber) ?? false;

		await emService.toggleLongHeadCrease(machine.id, longHeadNumber, isActive);

		setMachine((currentMachine) => ({
			...currentMachine,
			creaseTools: isActive
				? (currentMachine.creaseTools ?? []).filter((number) => number !== longHeadNumber)
				: [...(currentMachine.creaseTools ?? []), longHeadNumber],
		}));
	};

	const handleToggleLongHeadLatchClicked = async (longHeadNumber: number) => {
		const isActive = machine.activeLatches?.includes(longHeadNumber) ?? false;

		await emService.toggleLongHeadLatch(machine.id, longHeadNumber, isActive);

		setMachine((currentMachine) => ({
			...currentMachine,
			activeLatches: isActive
				? (currentMachine.activeLatches ?? []).filter((number) => number !== longHeadNumber)
				: [...(currentMachine.activeLatches ?? []), longHeadNumber],
		}));
	};

	const handleToggleLongHeadCutClicked = async (longHeadNumber: number) => {
		const isActive = machine.cutTools?.includes(longHeadNumber) ?? false;

		await emService.toggleLongHeadCut(machine.id, longHeadNumber, isActive);

		setMachine((currentMachine) => ({
			...currentMachine,
			cutTools: isActive
				? (currentMachine.cutTools ?? []).filter((number) => number !== longHeadNumber)
				: [...(currentMachine.cutTools ?? []), longHeadNumber],
		}));
	};

	const handleToggleTrackBrakeClicked = async (trackNumber: number) => {
		const isActive = machine.brakeTools?.includes(trackNumber) ?? false;

		await emService.toggleTrackBrake(machine.id, trackNumber, isActive);

		setMachine((currentMachine) => ({
			...currentMachine,
			brakeTools: isActive
				? (currentMachine.brakeTools ?? []).filter((number) => number !== trackNumber)
				: [...(currentMachine.brakeTools ?? []), trackNumber],
		}));
	};

	const handleToggleTrackRollerClicked = async (trackNumber: number) => {
		const isActive = machine.rollerTools?.includes(trackNumber) ?? false;

		await emService.toggleTrackRoller(machine.id, trackNumber, isActive);

		setMachine((currentMachine) => ({
			...currentMachine,
			rollerTools: isActive
				? (currentMachine.rollerTools ?? []).filter((number) => number !== trackNumber)
				: [...(currentMachine.rollerTools ?? []), trackNumber],
		}));
	};

	const handleToggleCrossHeadCreaseClicked = async () => {
		const isActive = machine.activeCrossHeadCrease ?? false;

		await emService.toggleCrossHeadCrease(machine.id, isActive);

		setMachine((currentMachine) => ({ ...currentMachine, activeCrossHeadCrease: !isActive }));
	};

	const handleToggleCrossHeadLatchClicked = async () => {
		const isActive = machine.activeCrossHeadLatch ?? false;

		await emService.toggleCrossHeadLatch(machine.id, isActive);

		setMachine((currentMachine) => ({ ...currentMachine, activeCrossHeadLatch: !isActive }));
	};

	const handleToggleCrossHeadCutClicked = async () => {
		const isActive = machine.activeCrossHeadCut ?? false;

		await emService.toggleCrossHeadCut(machine.id, isActive);

		setMachine((currentMachine) => ({ ...currentMachine, activeCrossHeadCut: !isActive }));
	};

	const handleCycleCrossHeadToolsClicked = () => {
		return emService.cycleCrossHeadTools(machine.id);
	};

	const handleCycleLongHeadToolsClicked = () => {
		return emService.cycleLongHeadTools(machine.id);
	};

	const handleCycleTrackToolsClicked = () => {
		return emService.cycleTrackTools(machine.id);
	};

	return (
		<EmServiceTemplate
			machine={machine}
			onToggleLongHeadCreaseClicked={handleToggleLongHeadCreaseClicked}
			onToggleLongHeadLatchClicked={handleToggleLongHeadLatchClicked}
			onToggleLongHeadCutClicked={handleToggleLongHeadCutClicked}
			onToggleTrackBrakeClicked={handleToggleTrackBrakeClicked}
			onToggleTrackRollerClicked={handleToggleTrackRollerClicked}
			onToggleCrossHeadCreaseClicked={handleToggleCrossHeadCreaseClicked}
			onToggleCrossHeadLatchClicked={handleToggleCrossHeadLatchClicked}
			onToggleCrossHeadCutClicked={handleToggleCrossHeadCutClicked}
			onCycleCrossHeadToolsClicked={handleCycleCrossHeadToolsClicked}
			onCycleLongHeadToolsClicked={handleCycleLongHeadToolsClicked}
			onCycleTrackToolsClicked={handleCycleTrackToolsClicked}
		/>
	);
};
